import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import * as authAPI from '@/api/auth/api';
import { appRoutes, consumeAuthReturnPath, navigateTo, saveAuthReturnPath } from '@/app/routes';
import { config } from '@/config';
import { mapSessionUserDTO } from '@/models/auth.mapper';
import { AuthenticatedUser } from '@/models/auth.model';
import { clearAccessToken, setAccessToken } from '@/utils/authToken';
import { decodeJwtPayload, isJwtExpiredOrExpiringSoon } from '@/utils/jwt';
import { createLogger } from '@/utils/logging';

const logger = createLogger('AuthStore', 'hook');
/** Persisted Entra access token for this app (OBO to Graph); short-lived. */
const MICROSOFT_OBO_STORAGE_KEY = 'blend.auth.microsoft_obo_assertion';
/** Counts SSO redirects to Entra within a browser session to detect infinite loops. */
const SSO_ATTEMPT_KEY = 'blend.auth.sso_attempt_count';
const SSO_MAX_ATTEMPTS = 3;
const processedCallbackKeys = new Set<string>();

/** All states the authentication flow can be in at any point in time. */
type AuthStatus =
  | 'initializing'
  | 'unauthenticated'
  | 'restoring'
  | 'redirecting'
  | 'processing-callback'
  | 'authenticated'
  | 'error';

/** Shape of the authentication reducer state. */
interface AuthState {
  user: AuthenticatedUser | null;
  accessToken: string | null;
  microsoftOboAssertion: string | null;
  status: AuthStatus;
  error: string | null;
  sessionExpired: boolean;
}

/** How often the poller checks token expiry (ms). */
const SESSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
/** Refresh OBO or warn about app-JWT expiry when this many seconds remain. */
const SESSION_EXPIRY_WARN_SECONDS = 3 * 60;

enum AuthActionType {
  AUTH_START = 'AUTH_START',
  AUTH_SUCCESS = 'AUTH_SUCCESS',
  OBO_UPDATED = 'OBO_UPDATED',
  AUTH_ERROR = 'AUTH_ERROR',
  LOGOUT = 'LOGOUT',
  NO_SESSION = 'NO_SESSION',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}

type AuthAction =
  | { type: AuthActionType.AUTH_START; payload: { status: AuthStatus } }
  | {
      type: AuthActionType.AUTH_SUCCESS;
      payload: {
        user: AuthenticatedUser;
        accessToken: string;
        microsoftOboAssertion: string | null;
      };
    }
  | { type: AuthActionType.OBO_UPDATED; payload: { microsoftOboAssertion: string | null } }
  | { type: AuthActionType.AUTH_ERROR; payload: { error: string } }
  | { type: AuthActionType.LOGOUT }
  | { type: AuthActionType.NO_SESSION }
  | { type: AuthActionType.SESSION_EXPIRED };

const initialState: AuthState = {
  user: null,
  accessToken: null,
  microsoftOboAssertion: null,
  status: 'initializing',
  error: null,
  sessionExpired: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case AuthActionType.AUTH_START:
      return {
        ...state,
        status: action.payload.status,
        error: null,
      };
    case AuthActionType.AUTH_SUCCESS:
      return {
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        microsoftOboAssertion: action.payload.microsoftOboAssertion,
        status: 'authenticated',
        error: null,
        sessionExpired: false,
      };
    case AuthActionType.OBO_UPDATED:
      return { ...state, microsoftOboAssertion: action.payload.microsoftOboAssertion };
    case AuthActionType.AUTH_ERROR:
      return {
        ...state,
        user: null,
        accessToken: null,
        microsoftOboAssertion: null,
        status: 'error',
        error: action.payload.error,
      };
    case AuthActionType.LOGOUT:
    case AuthActionType.NO_SESSION:
      return {
        user: null,
        accessToken: null,
        microsoftOboAssertion: null,
        status: 'unauthenticated',
        error: null,
        sessionExpired: false,
      };
    case AuthActionType.SESSION_EXPIRED:
      return {
        ...state,
        user: null,
        accessToken: null,
        microsoftOboAssertion: null,
        status: 'unauthenticated',
        sessionExpired: true,
      };
    default:
      return state;
  }
};

/** Everything exposed through AuthContext — state plus imperative helpers. */
interface AuthContextType extends AuthState {
  isAuthenticated: boolean;
  startSso: () => Promise<void>;
  refreshMicrosoftOboAssertion: () => Promise<string | null>;
  logout: () => void;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Props for the AuthProvider component. */
export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provides authentication state and SSO actions to the React tree.
 * On mount it detects an SSO callback (code + state params), or attempts to restore an
 * existing session via the httpOnly cookie (no localStorage involved).
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const logout = useCallback(() => {
    clearAccessToken();
    sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
    sessionStorage.removeItem(SSO_ATTEMPT_KEY);
    // Clear the httpOnly session cookie via the backend logout endpoint.
    void authAPI.logoutSession().catch(() => {
      // Best-effort — even if the request fails the local state is already cleared.
    });
    dispatch({ type: AuthActionType.LOGOUT });
    logger.info('User logged out');
  }, []);

  const finalizeCallback = useCallback(async (code: string, callbackState: string) => {
    const callbackKey = `${code}:${callbackState}`;
    if (processedCallbackKeys.has(callbackKey)) {
      logger.info('Skipping duplicate SSO callback processing');
      return;
    }
    processedCallbackKeys.add(callbackKey);

    dispatch({ type: AuthActionType.AUTH_START, payload: { status: 'processing-callback' } });

    try {
      const callbackResponse = await authAPI.completeSsoCallback(code, callbackState);
      const user = mapSessionUserDTO(callbackResponse.user);
      setAccessToken(callbackResponse.access_token);
      const obo = callbackResponse.microsoft_obo_assertion?.trim() || null;
      // OBO assertion is held in React state only — never written to sessionStorage.
      // On page refresh, refreshMicrosoftOboAssertion() re-fetches it from the backend
      // using the active httpOnly cookie session. Remove any legacy value just in case.
      sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);

      sessionStorage.removeItem(SSO_ATTEMPT_KEY);
      dispatch({
        type: AuthActionType.AUTH_SUCCESS,
        payload: {
          user,
          accessToken: callbackResponse.access_token,
          microsoftOboAssertion: obo,
        },
      });

      if (['dev', 'development'].includes(config.environment)) {
        const jwtClaims = decodeJwtPayload(callbackResponse.access_token);
        if (jwtClaims) {
          const authBlock = jwtClaims['https://blend360.com/auth'] as
            Record<string, unknown> | undefined;
          logger.debug('[dev] JWT claims issued', {
            sub: jwtClaims['sub'],
            email_domain: (jwtClaims['email'] as string | undefined)?.split('@')[1] ?? null,
            level: authBlock?.['level'],
            role: authBlock?.['role'],
            iat: jwtClaims['iat'],
            exp: jwtClaims['exp'],
          });
        }
      }

      const url = new URL(globalThis.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      globalThis.history.replaceState({}, document.title, url.toString());
      const next = consumeAuthReturnPath() ?? appRoutes.portfolio;
      navigateTo(next, true);
      logger.info('SSO callback completed successfully');
    } catch (error) {
      processedCallbackKeys.delete(callbackKey);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to complete SSO callback';
      clearAccessToken();
      sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
      dispatch({ type: AuthActionType.AUTH_ERROR, payload: { error: errorMessage } });
      logger.error('Failed to complete SSO callback', error);
    }
  }, []);

  const restoreSession = useCallback(async () => {
    dispatch({ type: AuthActionType.AUTH_START, payload: { status: 'restoring' } });

    let storedObo = sessionStorage.getItem(MICROSOFT_OBO_STORAGE_KEY)?.trim() || null;
    // Clear immediately after reading — OBO lives in memory only from this point forward.
    // sessionStorage is readable by any XSS payload; one-shot read + delete shrinks the exposure window.
    sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
    if (storedObo && isJwtExpiredOrExpiringSoon(storedObo, { skewSeconds: 0 })) {
      storedObo = null;
    }

    try {
      // The httpOnly cookie is sent automatically (withCredentials: true on the axios client).
      // The backend echoes the validated JWT in the response body so we can restore the
      // in-memory token state without ever touching localStorage.
      const sessionResponse = await authAPI.getSession();
      const user = mapSessionUserDTO(sessionResponse.user);
      setAccessToken(sessionResponse.access_token);
      dispatch({
        type: AuthActionType.AUTH_SUCCESS,
        payload: {
          user,
          accessToken: sessionResponse.access_token,
          microsoftOboAssertion: storedObo,
        },
      });
      logger.info('Session restored from cookie');
    } catch (error) {
      clearAccessToken();
      sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
      dispatch({ type: AuthActionType.NO_SESSION });
      logger.info('No active session found', error);
    }
  }, []);

  const startSso = useCallback(async () => {
    // Detect infinite SSO redirect loops — each call to startSso increments the counter.
    // The counter lives in sessionStorage so it survives the Entra redirect round-trip.
    // It is cleared on successful login or explicit logout.
    const attempts = parseInt(sessionStorage.getItem(SSO_ATTEMPT_KEY) ?? '0', 10);
    if (attempts >= SSO_MAX_ATTEMPTS) {
      sessionStorage.removeItem(SSO_ATTEMPT_KEY);
      clearAccessToken();
      sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
      logger.error('SSO redirect loop detected — aborting after max attempts', { attempts });
      dispatch({
        type: AuthActionType.AUTH_ERROR,
        payload: {
          error: 'Sign-in failed after multiple attempts. Please refresh the page and try again.',
        },
      });
      return;
    }
    sessionStorage.setItem(SSO_ATTEMPT_KEY, String(attempts + 1));

    dispatch({ type: AuthActionType.AUTH_START, payload: { status: 'redirecting' } });

    try {
      saveAuthReturnPath();
      const response = await authAPI.startSso(config.ssoProvider);
      logger.info('Redirecting to SSO provider', { provider: config.ssoProvider });
      globalThis.location.assign(response.authorization_url);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start SSO flow';
      dispatch({ type: AuthActionType.AUTH_ERROR, payload: { error: errorMessage } });
      logger.error('Failed to start SSO flow', error);
    }
  }, []);

  const refreshMicrosoftOboAssertion = useCallback(async (): Promise<string | null> => {
    if (!state.user) {
      return null;
    }
    try {
      const response = await authAPI.refreshMicrosoftOboAssertion();
      const obo = response.microsoft_obo_assertion?.trim() || null;
      // OBO is held in React state only after the initial session restore; never re-persist to
      // sessionStorage on refresh so the token is not readable by XSS during its valid lifetime.
      sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
      dispatch({
        type: AuthActionType.OBO_UPDATED,
        payload: { microsoftOboAssertion: obo },
      });
      return obo;
    } catch (error) {
      // A 400 or 401 means the server rejected the token — session is unrecoverable.
      // Sign out and redirect to login so the user can re-authenticate cleanly.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 400 || status === 401) {
        logger.warn('OBO refresh rejected by server — forcing sign-out and re-login', { status });
        logout();
        navigateTo(appRoutes.login);
        return null;
      }
      logger.error('Failed to refresh Microsoft OBO assertion', error);
      return null;
    }
  }, [state.user, logout]);

  const clearSessionExpired = useCallback(() => {
    dispatch({ type: AuthActionType.NO_SESSION });
  }, []);

  // Session poller: every 5 min, proactively refresh OBO if expiring within 3 min.
  // If our own app JWT is expiring, mark the session as expired so the UI can warn.
  useEffect(() => {
    if (state.status !== 'authenticated') {
      return;
    }

    const intervalId = setInterval(() => {
      const appToken = state.accessToken;
      if (
        appToken &&
        isJwtExpiredOrExpiringSoon(appToken, { skewSeconds: SESSION_EXPIRY_WARN_SECONDS })
      ) {
        logger.warn('App JWT expiring soon — marking session expired');
        clearAccessToken();
        sessionStorage.removeItem(MICROSOFT_OBO_STORAGE_KEY);
        dispatch({ type: AuthActionType.SESSION_EXPIRED });
        return;
      }
      const obo = state.microsoftOboAssertion;
      if (obo && isJwtExpiredOrExpiringSoon(obo, { skewSeconds: SESSION_EXPIRY_WARN_SECONDS })) {
        logger.info('OBO assertion expiring soon — proactively refreshing');
        void refreshMicrosoftOboAssertion().then((result) => {
          if (!result) {
            // OBO refresh failed but the app JWT is still valid — clear the OBO
            // assertion so downstream Graph calls degrade gracefully rather than
            // forcing a full re-login while the user still has an active session.
            logger.warn(
              'OBO refresh returned no token — clearing OBO assertion, app session remains active',
            );
            dispatch({
              type: AuthActionType.OBO_UPDATED,
              payload: { microsoftOboAssertion: null },
            });
          }
        });
      }
    }, SESSION_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [state.status, state.accessToken, state.microsoftOboAssertion, refreshMicrosoftOboAssertion]);

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const code = params.get('code');
    const callbackState = params.get('state');

    if (code && callbackState) {
      void finalizeCallback(code, callbackState);
      return;
    }

    // Attempt to restore session via the httpOnly cookie — no localStorage read needed.
    void restoreSession();
  }, [finalizeCallback, restoreSession]);

  const contextValue: AuthContextType = useMemo(
    () => ({
      ...state,
      isAuthenticated: Boolean(state.user && state.accessToken),
      startSso,
      refreshMicrosoftOboAssertion,
      logout,
      clearSessionExpired,
    }),
    [state, startSso, refreshMicrosoftOboAssertion, logout, clearSessionExpired],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

/**
 * Accesses authentication state and actions from the nearest AuthProvider.
 * Throws if called outside an AuthProvider.
 *
 * @returns The full AuthContextType including user, tokens, status, and SSO helpers.
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
